import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, ArrowLeft, Save, X, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { testimonialService } from '../services/api';
import { TestimonialItem } from '../types/api';
import LoadingSpinner from './ui/LoadingSpinner';

const AdminTestimonials: React.FC = () => {
  const [testimonialsData, setTestimonialsData] = useState<TestimonialItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState<TestimonialItem | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<Partial<TestimonialItem>>({
    name: '',
    profession: '',
    quote: '',
    rating: 5
  });

  // Carregar dados ao montar o componente
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const response = await testimonialService.getAll();
        if (response.success && response.data) {
          setTestimonialsData(response.data);
        } else {
          console.error('Erro ao carregar Testimonials:', response.error);
        }
      } catch (error) {
        console.error('Erro ao carregar Testimonials:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleAddNew = () => {
    setFormData({
      name: '',
      profession: '',
      quote: '',
      rating: 5
    });
    setEditingTestimonial(null);
    setIsEditing(false);
    setShowForm(true);
  };

  const handleEdit = (testimonial: TestimonialItem) => {
    setFormData(testimonial);
    setEditingTestimonial(testimonial);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir este depoimento?')) {
      try {
        const response = await testimonialService.delete(id);
        if (response.success) {
          setTestimonialsData(prev => prev.filter(testimonial => testimonial.id !== id));
          alert('Depoimento excluído com sucesso!');
        } else {
          alert('Erro ao excluir depoimento: ' + response.error);
        }
      } catch (error) {
        console.error('Erro ao deletar Testimonial:', error);
        alert('Erro ao excluir depoimento. Tente novamente.');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      let response: any;
      
      if (isEditing && editingTestimonial) {
        // Editar depoimento existente
        response = await testimonialService.update(editingTestimonial.id, formData);
        if (response.success && response.data) {
          setTestimonialsData(prev => prev.map(testimonial => testimonial.id === editingTestimonial.id ? response.data! : testimonial));
          alert('Depoimento atualizado com sucesso!');
        } else {
          alert('Erro ao atualizar depoimento: ' + response.error);
          return;
        }
      } else {
        // Criar novo depoimento
        response = await testimonialService.create({
          name: formData.name || '',
          profession: formData.profession || '',
          quote: formData.quote || '',
          rating: formData.rating || 5
        });
        if (response.success && response.data) {
          setTestimonialsData(prev => [...prev, response.data!]);
          alert('Depoimento criado com sucesso!');
        } else {
          alert('Erro ao criar depoimento: ' + response.error);
          return;
        }
      }
      
      setShowForm(false);
      setFormData({});
      setEditingTestimonial(null);
      setIsEditing(false);
    } catch (error) {
      console.error('Erro ao salvar Testimonial:', error);
      alert('Erro ao salvar depoimento. Tente novamente.');
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  if (loading) {
    return (
      <section className="section bg-brand-sand pt-32 min-h-screen">
        <div className="wrapper">
          <LoadingSpinner message="Carregando depoimentos..." />
        </div>
      </section>
    );
  }

  return (
    <section className="section bg-brand-sand pt-32 min-h-screen">
      <div className="wrapper">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="max-w-6xl mx-auto"
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="btn-secondary">
                <ArrowLeft className="w-4 h-4" />
                Voltar
              </Link>
              <div>
                <h1 className="text-3xl font-heading text-brand-slate">Gerenciar Depoimentos</h1>
                <p className="text-brand-ink/70">Adicionar, editar e excluir depoimentos de clientes</p>
              </div>
            </div>
            <button onClick={handleAddNew} className="btn-primary">
              <Plus className="w-4 h-4" />
              Novo Depoimento
            </button>
          </motion.div>

          {/* Form Modal */}
          {showForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-heading text-brand-slate">
                    {isEditing ? 'Editar Depoimento' : 'Novo Depoimento'}
                  </h2>
                  <button
                    onClick={() => setShowForm(false)}
                    className="text-brand-ink/60 hover:text-brand-ink"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Nome e Profissão */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <label className="text-sm font-medium text-brand-slate">Nome</label>
                      <input
                        type="text"
                        value={formData.name || ''}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-3 border-b-2 border-brand-slate/20 focus:border-brand-accent focus:outline-none text-brand-slate placeholder-brand-ink/40 transition-colors bg-transparent"
                        placeholder="Digite o nome da pessoa"
                        required
                      />
                    </div>
                    <div className="space-y-3">
                      <label className="text-sm font-medium text-brand-slate">Profissão</label>
                      <input
                        type="text"
                        value={formData.profession || ''}
                        onChange={(e) => setFormData({ ...formData, profession: e.target.value })}
                        className="w-full px-4 py-3 border-b-2 border-brand-slate/20 focus:border-brand-accent focus:outline-none text-brand-slate placeholder-brand-ink/40 transition-colors bg-transparent"
                        placeholder="Digite a profissão"
                        required
                      />
                    </div>
                  </div>

                  {/* Depoimento */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-brand-slate">Depoimento</label>
                    <textarea
                      value={formData.quote || ''}
                      onChange={(e) => setFormData({ ...formData, quote: e.target.value })}
                      className="w-full px-4 py-3 border-b-2 border-brand-slate/20 focus:border-brand-accent focus:outline-none text-brand-slate placeholder-brand-ink/40 transition-colors resize-y min-h-[120px] bg-transparent"
                      rows={4}
                      placeholder="Digite o depoimento completo"
                      required
                    />
                  </div>

                  {/* Avaliação */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-brand-slate">Avaliação (1-5 estrelas)</label>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setFormData({ ...formData, rating: star })}
                          className={`p-2 rounded-lg transition-colors ${
                            star <= (formData.rating || 0)
                              ? 'text-yellow-500 bg-yellow-50'
                              : 'text-gray-300 hover:text-yellow-400'
                          }`}
                        >
                          <Star className="w-6 h-6" />
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-4 pt-4">
                    <button type="submit" className="btn-primary flex-1">
                      <Save className="w-4 h-4" />
                      {isEditing ? 'Salvar Alterações' : 'Criar Depoimento'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowForm(false)}
                      className="btn-secondary"
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}

          {/* Testimonials List */}
          <motion.div variants={itemVariants} className="space-y-4">
            {testimonialsData.map((testimonial) => (
              <div key={testimonial.id} className="card card-pad">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-heading text-brand-slate">
                        {testimonial.name}
                      </h3>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-4 h-4 ${
                              star <= testimonial.rating
                                ? 'text-yellow-500 fill-current'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                    <p className="text-brand-ink/70 text-sm mb-2">
                      {testimonial.profession}
                    </p>
                    <p className="text-brand-ink/80 text-sm line-clamp-3">
                      "{testimonial.quote}"
                    </p>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleEdit(testimonial)}
                      className="p-2 text-brand-slate hover:text-brand-accent transition-colors"
                      title="Editar"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(testimonial.id)}
                      className="p-2 text-red-500 hover:text-red-700 transition-colors"
                      title="Excluir"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default AdminTestimonials;
